/**
 * Decizia de consimtamant, asa cum o tine browserul.
 *
 * Regula care da forma intregului fisier: **browserul e autoritatea, nu
 * serverul**. Decizia se aplica local in momentul in care omul apasa, si abia
 * apoi pleaca spre jurnal. Daca ar fi invers — daca urmarirea ar porni dupa ce
 * serverul confirma — atunci o eroare de retea ar produce urmarire neconsimtita,
 * si nimeni nu ar afla.
 *
 * A doua regula: **absenta unei decizii NU e un refuz si nu e o acceptare.** E o
 * a treia stare, care arata bannerul. Confuzia dintre „nu a decis" si „a refuzat"
 * e exact defectul din POS, unde vizitatorii din afara UE ramaneau pe `false`
 * implicit, nu vedeau niciodata bannerul, si deci nu puteau nici accepta nici
 * refuza — iar tot tracking-ul era mort fara ca nimeni sa stie.
 */

import {
  CONSENT_UNDECIDED,
  consentIsCurrent,
  type ConsentDecision,
  type ConsentState,
} from "@deliveryapp/shared";

const STORAGE_PREFIX = "deliveryapp.consent.v1";
const ANON_PREFIX = "deliveryapp.anon.v1";

function decisionKey(marketKey: string): string {
  return `${STORAGE_PREFIX}.${marketKey}`;
}

function anonKey(marketKey: string): string {
  return `${ANON_PREFIX}.${marketKey}`;
}

/**
 * Identificatorul anonim al vizitatorului.
 *
 * NU e un identificator de urmarire, si diferenta conteaza: se genereaza local,
 * nu pleaca la niciun tert, nu circula intre piete (e prefixat cu piata) si
 * exista dintr-un singur motiv — ca randul din jurnal sa poata fi legat de
 * bannerul care l-a produs. Fara el, jurnalul ar contine decizii fara subiect.
 *
 * Se creeaza la prima citire, INAINTE de orice decizie, fiindca si un refuz are
 * nevoie de subiect ca sa fie inregistrabil.
 */
export function readAnonymousId(marketKey: string): string {
  const key = anonKey(marketKey);
  try {
    const existing = window.localStorage.getItem(key);
    if (typeof existing === "string" && existing.length > 0) return existing;
  } catch {
    // Stocare indisponibila (mod privat, cota plina). Continuam cu un
    // identificator efemer: decizia se aplica in sesiunea curenta si se pierde
    // la reincarcare. E singura degradare corecta — alternativa ar fi sa nu
    // putem inregistra nici macar refuzul.
    return generateId();
  }
  const fresh = generateId();
  try {
    window.localStorage.setItem(key, fresh);
  } catch {
    // vezi mai sus
  }
  return fresh;
}

function generateId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi !== undefined && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  if (cryptoApi !== undefined && typeof cryptoApi.getRandomValues === "function") {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  // Browser fara Web Crypto: identificatorul nu are nevoie sa fie imposibil de
  // ghicit, doar sa nu se ciocneasca. Nu se foloseste la nimic care sa cantareasca
  // securitate.
  return `f${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Decizia stocata, DACA mai e valabila pe textul curent.
 *
 * O decizie luata pe o versiune veche de text nu se pastreaza tacit: omul a
 * consimtit la altceva decat scrie acum. Se intoarce `null`, deci bannerul apare
 * din nou.
 */
export function readConsent(marketKey: string, textVersion: string): ConsentState {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(decisionKey(marketKey));
  } catch {
    return CONSENT_UNDECIDED;
  }
  if (raw === null || raw.length === 0) return CONSENT_UNDECIDED;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Rand corupt (scriere intrerupta, editare manuala). Il tratam ca pe o
    // absenta de decizie si intrebam din nou — a presupune „acceptat" ar porni
    // urmarirea pe baza unui octet stricat.
    return CONSENT_UNDECIDED;
  }

  const decision = asDecision(parsed);
  if (decision === null) return CONSENT_UNDECIDED;
  return consentIsCurrent(decision, textVersion) ? decision : CONSENT_UNDECIDED;
}

function asDecision(value: unknown): ConsentDecision | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.analytics !== "boolean") return null;
  if (typeof record.marketing !== "boolean") return null;
  if (typeof record.textVersion !== "string" || record.textVersion.length === 0) return null;
  if (typeof record.decidedAt !== "string" || record.decidedAt.length === 0) return null;
  return {
    analytics: record.analytics,
    marketing: record.marketing,
    textVersion: record.textVersion,
    decidedAt: record.decidedAt,
  };
}

export function writeConsent(marketKey: string, decision: ConsentDecision): void {
  try {
    window.localStorage.setItem(decisionKey(marketKey), JSON.stringify(decision));
  } catch {
    // Decizia ramane activa in memorie pentru sesiunea curenta. Nu are rost sa
    // i se spuna clientului ca „nu s-a putut salva": ce a ales se respecta acum,
    // iar la urmatoarea vizita va fi intrebat din nou.
  }
}

/**
 * Sterge decizia. Folosita cand piata isi schimba textele si vrem sa reintrebam,
 * si de butonul de resetare din preferinte.
 */
export function clearConsent(marketKey: string): void {
  try {
    window.localStorage.removeItem(decisionKey(marketKey));
  } catch {
    // nimic de facut, si nimic de raportat
  }
}
